import React, { useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { useLocation, useNavigationType, createRoutesFromChildren, matchRoutes } from 'react-router-dom';
import { Provider } from 'react-redux';
import * as Sentry from '@sentry/react';
import { ConfigProvider } from 'antd';

import { store } from '#core/store';
import { api as apiAuth } from '#adapter/api/auth';
import { rtkApi } from '#adapter/api/campaigns/v5/suppliers';
import { env } from '#helper/env';

import { Routes } from '#route';

import '@portals/portal-fonts/hauss.css';
import '#locale';
import './index.pcss';
import environment from '#environment';

import { theme } from './environments/antd-theme';

if(env(import.meta.env.APP_SENTRY) !== false) {
    Sentry.init({
        dsn                     : import.meta.env.APP_SENTRY_DSN,
        maxBreadcrumbs          : env(import.meta.env.APP_SENTRY_MAX_BREADCRUMBS) ?? 50,
        debug                   : env(import.meta.env.APP_SENTRY_DEBUG) ?? import.meta.env.DEV,
        environment             : window.location.hostname,
        release                 : __VERSION__,
        attachStacktrace        : env(import.meta.env.APP_SENTRY_ATTACH_STACK_TRACE),
        tracesSampleRate        : 1.0,
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
        autoSessionTracking     : env(import.meta.env.APP_SENTRY_AUTO_SESSION_TRACKING),
        integrations            : [
            Sentry.reactRouterV6BrowserTracingIntegration({
                useEffect,
                useLocation,
                useNavigationType,
                createRoutesFromChildren,
                matchRoutes
            }),
            Sentry.replayIntegration({
                maskAllText  : false,
                maskAllInputs: false,
                blockAllMedia: false,
                mask         : ['[data-mask]', '[data-sentry-mask]']
            }),
            Sentry.thirdPartyErrorFilterIntegration({
                filterKeys: [env(import.meta.env.APP_SENTRY_APPLICATION_KEY) ?? 'campaigns-new-frontend-sentry-key'],
                behaviour : 'drop-error-if-contains-third-party-frames'
            })
        ]
    });

    Sentry.setTags({
        hostname: window.location.hostname,
        mode    : import.meta.env.MODE
    });
}

store.dispatch(apiAuth.endpoints.check.initiate())
    .unwrap()
    .then(() => {
        return store.dispatch(rtkApi.endpoints.getApiV5Suppliers.initiate({}));
    })
    .then(() => {
        const container = document.getElementById(import.meta.env.APP_HTML_ROOT_ELEMENT_ID);

        if(container) {
            const root = createRoot(container);

            root.render(
                <ConfigProvider
                    theme={theme}
                >
                    <Provider store={store}>
                        <Routes />
                    </Provider>
                </ConfigProvider>
            );
        }
    })
    .catch(() => {
        // @TODO: DEV /login-v3
        window.location.href = `${environment.hostSellerAuth}?redirect_url=${window.location.href}`;
    });

console.info('App version: %s', __VERSION__);
